import { createContext, useContext, useState, useEffect, useCallback, useRef, type ReactNode } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { apiFetch } from "@/lib/apiFetch";
import { featureFlagsEnabledContract } from "@shared/contracts";

export interface FeatureFlag {
  id: number;
  key: string;
  name: string;
  description: string;
  enabled: boolean;
  createdAt: string;
}

export interface FlagMeta {
  key: string;
  name: string;
  description: string;
  prodDefault: boolean;
  tested: boolean;
  mvpComplete: boolean;
  category: string;
}

const FF_OVERRIDES_KEY = "ff_dev_overrides";

function readOverrides(): Record<string, boolean | null> {
  if (typeof localStorage === "undefined") return {};
  try {
    const raw = localStorage.getItem(FF_OVERRIDES_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

function writeOverrides(overrides: Record<string, boolean | null>): void {
  try {
    localStorage.setItem(FF_OVERRIDES_KEY, JSON.stringify(overrides));
  } catch { /* quota exceeded — silently skip */ }
}

interface FeatureFlagContextValue {
  flags: Record<string, boolean>;
  isLoaded: boolean;
  isFlagEnabled: (key: string) => boolean;
  devOverrides: Record<string, boolean | null>;
  setDevOverride: (key: string, value: boolean | null) => void;
  resetAllOverrides: () => void;
  applyProfileOverrides: (overrides: Record<string, boolean>) => void;
  flagMeta: FlagMeta[];
  showZoneIndicators: boolean;
  setShowZoneIndicators: (v: boolean) => void;
}

const FeatureFlagContext = createContext<FeatureFlagContextValue | null>(null);

const IS_DEV = import.meta.env.DEV;
const SHOW_ZONES_KEY = "ff_show_zones";

export function FeatureFlagProvider({ children }: { children: ReactNode }) {
  const qc = useQueryClient();

  const [devOverrides, setDevOverridesState] = useState<Record<string, boolean | null>>(
    IS_DEV ? readOverrides : () => ({})
  );

  const [showZoneIndicators, setShowZoneIndicatorsState] = useState<boolean>(() => {
    if (!IS_DEV) return false;
    try { return localStorage.getItem(SHOW_ZONES_KEY) === "1"; } catch { return false; }
  });

  const overrideCount = IS_DEV
    ? Object.values(devOverrides).filter((v) => v !== null).length
    : 0;

  const { data: flags = {}, isFetched } = useQuery<Record<string, boolean>>({
    queryKey: ["/api/feature-flags/enabled"],
    queryFn: async () => {
      try {
        return await apiFetch(featureFlagsEnabledContract);
      } catch {
        return {};
      }
    },
    staleTime: overrideCount > 0 ? Infinity : 60_000,
  });

  const { data: flagMeta = [] } = useQuery<FlagMeta[]>({
    queryKey: ["/api/feature-flags/meta"],
    queryFn: async () => {
      try {
        const res = await fetch("/api/feature-flags/meta");
        if (!res.ok) return [];
        return res.json();
      } catch {
        return [];
      }
    },
    staleTime: Infinity,
    enabled: IS_DEV,
  });

  const staleTimeRef = useRef(overrideCount > 0 ? Infinity : 60_000);
  useEffect(() => {
    const newStaleTime = overrideCount > 0 ? Infinity : 60_000;
    if (newStaleTime !== staleTimeRef.current) {
      staleTimeRef.current = newStaleTime;
      qc.setQueryDefaults(["/api/feature-flags/enabled"], { staleTime: newStaleTime });
    }
  }, [overrideCount, qc]);

  useEffect(() => {
    if (!IS_DEV) return;
    const handler = (e: StorageEvent) => {
      if (e.key === FF_OVERRIDES_KEY) {
        setDevOverridesState(readOverrides());
      }
      if (e.key === SHOW_ZONES_KEY) {
        try { setShowZoneIndicatorsState(localStorage.getItem(SHOW_ZONES_KEY) === "1"); } catch {}
      }
    };
    window.addEventListener("storage", handler);
    return () => window.removeEventListener("storage", handler);
  }, []);

  const setDevOverride = useCallback((key: string, value: boolean | null) => {
    if (!IS_DEV) return;
    setDevOverridesState((prev) => {
      const next = { ...prev, [key]: value };
      writeOverrides(next);
      return next;
    });
  }, []);

  const resetAllOverrides = useCallback(() => {
    if (!IS_DEV) return;
    setDevOverridesState({});
    writeOverrides({});
    qc.setQueryDefaults(["/api/feature-flags/enabled"], { staleTime: 60_000 });
    qc.invalidateQueries({ queryKey: ["/api/feature-flags/enabled"] });
  }, [qc]);

  const applyProfileOverrides = useCallback((overrides: Record<string, boolean>) => {
    if (!IS_DEV) return;
    const next: Record<string, boolean | null> = { ...overrides };
    writeOverrides(next);
    setDevOverridesState(next);
    qc.invalidateQueries({ queryKey: ["/api/feature-flags/enabled"] });
  }, [qc]);

  const setShowZoneIndicators = useCallback((v: boolean) => {
    if (!IS_DEV) return;
    try { localStorage.setItem(SHOW_ZONES_KEY, v ? "1" : "0"); } catch {}
    setShowZoneIndicatorsState(v);
    window.dispatchEvent(new StorageEvent("storage", { key: SHOW_ZONES_KEY }));
  }, []);

  function isFlagEnabled(key: string): boolean {
    if (IS_DEV) {
      const override = devOverrides[key];
      if (override !== null && override !== undefined) return override;
    }
    return flags[key] ?? false;
  }

  return (
    <FeatureFlagContext.Provider value={{
      flags,
      isLoaded: isFetched,
      isFlagEnabled,
      devOverrides,
      setDevOverride,
      resetAllOverrides,
      applyProfileOverrides,
      flagMeta,
      showZoneIndicators,
      setShowZoneIndicators,
    }}>
      {children}
    </FeatureFlagContext.Provider>
  );
}

export function useFeatureFlags() {
  const ctx = useContext(FeatureFlagContext);
  if (!ctx) throw new Error("useFeatureFlags must be used within FeatureFlagProvider");
  return ctx;
}

export function useFeatureFlag(key: string): boolean {
  const ctx = useContext(FeatureFlagContext);
  if (!ctx) return false;
  return ctx.isFlagEnabled(key);
}
